/** Endpoints + hooks TanStack Query pour la ressource ClientFile. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  ProposedChange,
  ProposedChangeActionItem,
  ProposedChangeActionResult,
  ClientFileCreate,
  ClientFileEngagementType,
  ClientFileLifecycleTransition,
  ClientFileLifecycleStage,
  ClientFileQueueResponse,
  ClientFileRead,
  ClientFileUpdate,
  DossierOperationsSummary,
  ExtractionRunRead,
  LegalEntityCreate,
  LegalEntityRead,
  LegalEntityUpdate,
  NextActionConversionResult,
  NoteSuggestionRead,
  PartyRead,
} from "./schema";

export type ClientFileQueueSort =
  | "updated_at"
  | "next_action"
  | "next_review"
  | "patrimoine"
  | "compliance"
  | "attention"
  | "severity";

export interface ClientFileQueueParams {
  limit?: number;
  offset?: number;
  archive_status?: "active" | "archived" | "all";
  lifecycle_stage?: ClientFileLifecycleStage;
  owner_user_id?: string;
  unowned?: boolean;
  engagement_type?: ClientFileEngagementType;
  action_overdue?: boolean;
  review_due?: boolean;
  document_missing?: boolean;
  attention_severity?: "NONE" | "INFO" | "WARNING" | "DANGER";
  search?: string;
  sort?: ClientFileQueueSort;
  order?: "asc" | "desc";
}

function toQueryParams(params?: ClientFileQueueParams) {
  if (!params) return undefined;
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  ) as Record<string, string | number | boolean | null | undefined>;
}

// ---------------------------------------------------------------------------
// Endpoints — Client files
// ---------------------------------------------------------------------------

export const clientFilesApi = {
  list: (params?: { archive_status?: "active" | "archived" | "all" }) =>
    api.get<ClientFileRead[]>("/client-files", params),
  queue: (params?: ClientFileQueueParams) =>
    api.get<ClientFileQueueResponse>("/client-files/queue", toQueryParams(params)),
  get: (id: string) => api.get<ClientFileRead>(`/client-files/${id}`),
  create: (payload: ClientFileCreate) => api.post<ClientFileRead>("/client-files", payload),
  update: (id: string, payload: ClientFileUpdate) =>
    api.patch<ClientFileRead>(`/client-files/${id}`, payload),
  transitionLifecycle: (id: string, payload: ClientFileLifecycleTransition) =>
    api.post<ClientFileRead>(`/client-files/${id}/lifecycle-transition`, payload),
  // « Transformer en tâche » : l'action héritée de l'ancienne carte « Mission »
  // devient une tâche du dossier ; 409 s'il n'y a plus rien à transformer.
  convertNextActionToTask: (id: string) =>
    api.post<NextActionConversionResult>(`/client-files/${id}/next-action/convert-to-task`),
  // Pas de `delete` : le geste courant est l'archivage (transitionLifecycle).
  // L'effacement définitif (DELETE, ADMIN, dossier archivé) n'a pas d'UI.
  listParties: (id: string) => api.get<PartyRead[]>(`/client-files/${id}/parties`),
  // #222 — file « à réviser » au niveau dossier (suggestions + extractions IA).
  pendingSuggestions: (id: string) =>
    api.get<NoteSuggestionRead[]>(`/client-files/${id}/pending-suggestions`),
  pendingExtractions: (id: string) =>
    api.get<ExtractionRunRead[]>(`/client-files/${id}/pending-extractions`),
  // #454 — contrat unifié : remplace pendingSuggestions + pendingExtractions.
  proposedChanges: (id: string) =>
    api.get<ProposedChange[]>(`/client-files/${id}/proposed-changes`),
  // Revue actionnable — apply/reject par ligne, verdicts individuels, le lot
  // continue sur erreur (un 409 gardé bloque SA ligne, jamais les suivantes).
  actOnProposedChanges: (id: string, items: ProposedChangeActionItem[]) =>
    api.post<ProposedChangeActionResult[]>(`/client-files/${id}/proposed-changes/actions`, items),
  operationsSummary: (id: string) =>
    api.get<DossierOperationsSummary>(`/client-files/${id}/operations-summary`),
};

/** Préfixe partagé des queries « à réviser » — utilisé pour l'invalidation
 * croisée : toute mutation suggestion/extraction (déclenchée depuis une note)
 * rafraîchit aussi la file dossier. */
export const clientFilePendingKeys = {
  all: ["client-files", "pending"] as const,
  suggestions: (id: string) => ["client-files", "pending", "suggestions", id] as const,
  extractions: (id: string) => ["client-files", "pending", "extractions", id] as const,
  proposedChanges: (id: string) => ["client-files", "pending", "proposed-changes", id] as const,
};

export function usePendingSuggestions(cfId: string | undefined) {
  return useQuery({
    queryKey: cfId ? clientFilePendingKeys.suggestions(cfId) : ["client-files", "noop"],
    queryFn: () => clientFilesApi.pendingSuggestions(cfId as string),
    enabled: !!cfId,
  });
}

export function usePendingExtractions(cfId: string | undefined) {
  return useQuery({
    queryKey: cfId ? clientFilePendingKeys.extractions(cfId) : ["client-files", "noop"],
    queryFn: () => clientFilesApi.pendingExtractions(cfId as string),
    enabled: !!cfId,
  });
}

export function useProposedChanges(cfId: string | undefined) {
  return useQuery({
    queryKey: cfId ? clientFilePendingKeys.proposedChanges(cfId) : ["client-files", "noop"],
    queryFn: () => clientFilesApi.proposedChanges(cfId as string),
    enabled: !!cfId,
  });
}

/** Préfixes à rafraîchir après une action de revue : la file elle-même plus
 *  toutes les surfaces que les écritures ont pu toucher. */
const ACTION_INVALIDATE_PREFIXES: readonly (readonly string[])[] = [
  ["client-files"],
  ["notes"],
  ["persons"],
  ["investor-profile"],
  ["relationships"],
  ["tasks"],
  ["real-estate"],
  ["financial-assets"],
  ["loans"],
];

export function useActOnProposedChanges(cfId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: ProposedChangeActionItem[]) =>
      clientFilesApi.actOnProposedChanges(cfId as string, items),
    onSuccess: () => {
      for (const key of ACTION_INVALIDATE_PREFIXES) qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useClientFileParties(cfId: string | undefined) {
  return useQuery({
    queryKey: cfId ? ["client-files", "parties", cfId] : ["client-files", "noop"],
    queryFn: () => clientFilesApi.listParties(cfId as string),
    enabled: !!cfId,
  });
}

export const clientFileKeys = {
  all: ["client-files"] as const,
  list: (archiveStatus: "active" | "archived" | "all" = "active") =>
    [...clientFileKeys.all, "list", { archiveStatus }] as const,
  queue: (params: ClientFileQueueParams) => [...clientFileKeys.all, "queue", params] as const,
  detail: (id: string) => [...clientFileKeys.all, "detail", id] as const,
  operations: (id: string) => [...clientFileKeys.all, "operations", id] as const,
};

export function useClientFiles(archiveStatus: "active" | "archived" | "all" = "active") {
  return useQuery({
    queryKey: clientFileKeys.list(archiveStatus),
    queryFn: () => clientFilesApi.list({ archive_status: archiveStatus }),
  });
}

export function useClientFilesQueue(params: ClientFileQueueParams) {
  return useQuery({
    queryKey: clientFileKeys.queue(params),
    queryFn: () => clientFilesApi.queue(params),
  });
}

export function useClientFile(id: string | undefined) {
  return useQuery({
    queryKey: id ? clientFileKeys.detail(id) : ["client-files", "noop"],
    queryFn: () => clientFilesApi.get(id as string),
    enabled: !!id,
  });
}

export function useDossierOperations(id: string | undefined) {
  return useQuery({
    queryKey: id ? clientFileKeys.operations(id) : ["client-files", "operations", "noop"],
    queryFn: () => clientFilesApi.operationsSummary(id as string),
    enabled: !!id,
  });
}

export function useCreateClientFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientFileCreate) => clientFilesApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientFileKeys.all }),
  });
}

export function useUpdateClientFile(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientFileUpdate) => clientFilesApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientFileKeys.all }),
  });
}

export function useConvertNextActionToTask(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clientFilesApi.convertNextActionToTask(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientFileKeys.all });
      // La nouvelle tâche apparaît dans « À faire sur ce dossier » et /taches.
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useTransitionClientFileLifecycle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientFileLifecycleTransition) =>
      clientFilesApi.transitionLifecycle(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientFileKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// Endpoints — Legal entities
// ---------------------------------------------------------------------------

export const legalEntitiesApi = {
  list: () => api.get<LegalEntityRead[]>("/legal-entities"),
  get: (id: string) => api.get<LegalEntityRead>(`/legal-entities/${id}`),
  create: (payload: LegalEntityCreate) => api.post<LegalEntityRead>("/legal-entities", payload),
  update: (id: string, payload: LegalEntityUpdate) =>
    api.patch<LegalEntityRead>(`/legal-entities/${id}`, payload),
  delete: (id: string) => api.delete<void>(`/legal-entities/${id}`),
};

export const legalEntityKeys = {
  all: ["legal-entities"] as const,
  list: () => [...legalEntityKeys.all, "list"] as const,
  detail: (id: string) => [...legalEntityKeys.all, "detail", id] as const,
};

export function useLegalEntity(id: string | undefined) {
  return useQuery({
    queryKey: id ? legalEntityKeys.detail(id) : ["legal-entities", "noop"],
    queryFn: () => legalEntitiesApi.get(id as string),
    enabled: !!id,
  });
}

export function useUpdateLegalEntity(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LegalEntityUpdate) => legalEntitiesApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: legalEntityKeys.all }),
  });
}
